import React, { useContext, useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Popover, Drawer, Grid } from "antd";
import {
  User,
  Menu,
  Settings,
  LogOut,
  Hotel,
  Star,
  Briefcase,
  Megaphone,
  Inbox,
  FlaskConical,
  Lightbulb,
  Sparkles,
  ArrowRight,
  Building2,
  UserCheck,
} from "lucide-react";
import NewFeatAnn from "../assets/images/homePage/new-feat.svg";
import LogoSvg from "../assets/svg/logos/logo-black.svg"; // Using logo-black.svg (black text variant) since header has a light background
import { AuthContext } from "../context/AuthContext";
import { getUserInfo } from "../utils/utils";
import { userRoles } from "../config";
import NotificationDropdown from "./NotificationDropdown";
import SearchData from "./SearchData";

const { useBreakpoint } = Grid;

const AppHeader = () => {
  const { isAuthenticated, isEmailSetupView, isWebToLeadView, logout } =
    useContext(AuthContext);
  const { role } = getUserInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const isCompanyDetailsPage = location.pathname.startsWith("/company-summary");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const showSetting = () => {
    navigate("/my-account");
  };

  const screens = useBreakpoint();
  const isMobile = isAuthenticated ? !screens.xl : !screens.lg;

  // Track scroll position to adjust header shadows/opacity dynamically
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const content = (
    <div className="tw-py-1 tw-px-2 tw-w-44">
      <button
        onClick={showSetting}
        className="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50 hover:tw-text-[#5D44FF] tw-rounded-lg tw-transition-colors tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer tw-flex tw-items-center tw-gap-2"
      >
        <User className="tw-w-4 tw-h-4" />
        My Account
      </button>

      {(isEmailSetupView || isWebToLeadView || role === userRoles?.admin) && (
        <button
          onClick={() =>
            navigate(
              role === userRoles?.admin
                ? "/settings/user-management"
                : isWebToLeadView
                  ? "/settings/web-to-lead"
                  : "/settings/email-setup",
            )
          }
          className="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50 hover:tw-text-[#5D44FF] tw-rounded-lg tw-transition-colors tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer tw-flex tw-items-center tw-gap-2"
        >
          <Settings className="tw-w-4 tw-h-4 tw-opacity-80" />
          Settings
        </button>
      )}

      <div className="tw-border-t tw-border-slate-100 tw-my-1" />

      <button
        onClick={logout}
        className="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-rose-600 hover:tw-bg-rose-50 tw-rounded-lg tw-transition-colors tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer tw-flex tw-items-center tw-gap-2"
      >
        <LogOut className="tw-w-4 tw-h-4" />
        Sign out
      </button>
    </div>
  );

  return (
    <>
      <header
        className={`tw-sticky tw-top-0 tw-z-50 tw-w-full tw-bg-white/95 tw-backdrop-blur-md tw-transition-all tw-duration-200 ${
          scrolled
            ? "tw-shadow-md tw-border-b tw-border-slate-100"
            : "tw-border-b tw-border-slate-100/60"
        }`}
      >
        <div className="tw-w-full tw-px-6 sm:tw-px-8 lg:tw-px-12 tw-h-20 tw-flex tw-items-center tw-justify-between tw-gap-4 xl:tw-gap-8">
          {/* Logo and Search Block */}
          <div className="tw-flex tw-items-center tw-gap-6 xl:tw-gap-10">
            <NavLink
              to={isAuthenticated ? "/dashboard-view" : "/"}
              className="tw-flex tw-items-center hover:tw-opacity-95 tw-transition-opacity"
            >
              <img
                src={LogoSvg}
                alt="BeyondLeadz"
                style={{ height: isMobile ? "18px" : "24px", width: "auto" }}
                className="tw-object-contain"
              />
            </NavLink>

            {!isMobile && (
              <div className="tw-hidden xl:tw-block tw-w-72 search-advance">
                <SearchData />
              </div>
            )}
          </div>

          {/* Desktop Navigation Links */}
          {!isMobile && (
            <nav className="tw-hidden lg:tw-flex tw-items-center tw-gap-4 xl:tw-gap-8">
              {!isAuthenticated && (
                <>
                  {/* Products Dropdown */}
                  <div className="tw-relative tw-group tw-py-2">
                    <button className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-text-[#5D44FF] tw-transition-colors tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer tw-whitespace-nowrap">
                      Products
                      <svg
                        className="tw-w-3.5 tw-h-3.5 tw-transition-transform tw-duration-200 group-hover:tw-rotate-180"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu Overlay */}
                    <div className="tw-absolute tw-left-1/2 tw-transform tw--translate-x-1/2 tw-top-full tw-w-[620px] tw-pt-3 tw-opacity-0 tw-invisible group-hover:tw-opacity-100 group-hover:tw-visible tw-transition-all tw-duration-200 tw-z-50">
                      <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-100/90 tw-rounded-3xl tw-shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] tw-p-6">
                        {/* Grid: Products List */}
                        <div className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-2">
                          <NavLink
                            to="/hospitality"
                            className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                          >
                            <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#2563EB] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                              <Hotel className="tw-w-5 tw-h-5" />
                            </div>
                            <div>
                              <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                                Hotel Lead Engine
                              </h5>
                              <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                                Discover direct booking opportunities with AI.
                              </p>
                            </div>
                          </NavLink>

                          <NavLink
                            to="/sales"
                            className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                          >
                            <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#059669] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                              <Briefcase className="tw-w-5 tw-h-5" />
                            </div>
                            <div>
                              <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                                Sales Pipeline Growth
                              </h5>
                              <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                                Track pursuits and forecast booking growth.
                              </p>
                            </div>
                          </NavLink>

                          <NavLink
                            to="/review-management"
                            className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                          >
                            <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#DC2626] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                              <Star className="tw-w-5 tw-h-5" />
                            </div>
                            <div>
                              <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                                Reputation &amp; Review
                              </h5>
                              <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                                Stay guest-ready and compliant with feedback.
                              </p>
                            </div>
                          </NavLink>

                          <NavLink
                            to="/marketing"
                            className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                          >
                            <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#7C3AED] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                              <Megaphone className="tw-w-5 tw-h-5" />
                            </div>
                            <div>
                              <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                                Marketing Automation
                              </h5>
                              <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                                Manage multi-channel campaigns centrally.
                              </p>
                            </div>
                          </NavLink>

                          <NavLink
                            to="/data-to-lead"
                            className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                          >
                            <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#0891B2] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                              <Inbox className="tw-w-5 tw-h-5" />
                            </div>
                            <div>
                              <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                                Data-to-Lead Integration
                              </h5>
                              <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                                Direct audience data integration.
                              </p>
                            </div>
                          </NavLink>

                          <NavLink
                            to="/lead-enrichment"
                            className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                          >
                            <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#D97706] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                              <FlaskConical className="tw-w-5 tw-h-5" />
                            </div>
                            <div>
                              <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                                AI-Lead Enrichment
                              </h5>
                              <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                                Auto-enrich contacts with AI intelligence.
                              </p>
                            </div>
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Data Search Dropdown */}
                  <div className="tw-relative tw-group tw-py-2">
                    <button className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-text-[#5D44FF] tw-transition-colors tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer tw-whitespace-nowrap">
                      Data Search
                      <svg
                        className="tw-w-3.5 tw-h-3.5 tw-transition-transform tw-duration-200 group-hover:tw-rotate-180"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu Overlay */}
                    <div className="tw-absolute tw-left-1/2 tw-transform tw--translate-x-1/2 tw-top-full tw-w-[400px] tw-pt-3 tw-opacity-0 tw-invisible group-hover:tw-opacity-100 group-hover:tw-visible tw-transition-all tw-duration-200 tw-z-50">
                      <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-100/90 tw-rounded-2xl tw-shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] tw-p-5 tw-space-y-2">
                        <NavLink
                          to="/search-company"
                          className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                        >
                          <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#2563EB] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                            <Building2 className="tw-w-5 tw-h-5" />
                          </div>
                          <div>
                            <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                              Company Search
                            </h5>
                            <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                              Discover direct company opportunities with AI.
                            </p>
                          </div>
                        </NavLink>

                        <NavLink
                          to="/search-executive"
                          className="tw-flex tw-gap-3.5 tw-items-start tw-p-3.5 tw-rounded-2xl hover:tw-bg-[#F8FAFC] tw-transition-all tw-duration-200 tw-group/item tw-text-decoration-none"
                        >
                          <div className="tw-w-11 tw-h-11 tw-rounded-[14px] tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-[#7C3AED] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-duration-200 group-hover/item:tw-bg-white group-hover/item:tw-border-slate-300 group-hover/item:tw-scale-105 group-hover/item:tw-shadow-xs">
                            <UserCheck className="tw-w-5 tw-h-5" />
                          </div>
                          <div>
                            <h5 className="tw-text-[13px] tw-font-bold tw-text-slate-900 group-hover/item:tw-text-[#5D44FF] tw-transition-colors tw-mb-0.5 tw-mt-0">
                              Executive Search
                            </h5>
                            <p className="tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-m-0">
                              Find verified decision makers &amp; leadership.
                            </p>
                          </div>
                        </NavLink>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <NavLink
                to={isAuthenticated ? "/search-lead" : "/lead-management"}
                className={({ isActive }) =>
                  `tw-text-sm tw-font-bold tw-transition-colors tw-text-decoration-none tw-whitespace-nowrap ${
                    isActive
                      ? "tw-text-[#5D44FF]"
                      : "tw-text-slate-600 hover:tw-text-[#5D44FF]"
                  }`
                }
              >
                Leads
              </NavLink>

              {!isAuthenticated && (
                <NavLink
                  to="/pricing"
                  className={({ isActive }) =>
                    `tw-text-sm tw-font-bold tw-transition-colors tw-text-decoration-none tw-whitespace-nowrap ${
                      isActive
                        ? "tw-text-[#5D44FF]"
                        : "tw-text-slate-600 hover:tw-text-[#5D44FF]"
                    }`
                  }
                >
                  Pricing
                </NavLink>
              )}

              {isAuthenticated && (
                <>
                  <NavLink
                    to="/dashboard-view"
                    className={({ isActive }) =>
                      `tw-text-sm tw-font-bold tw-transition-colors tw-text-decoration-none tw-whitespace-nowrap ${
                        isActive
                          ? "tw-text-[#5D44FF]"
                          : "tw-text-slate-600 hover:tw-text-[#5D44FF]"
                      }`
                    }
                  >
                    Dashboard
                  </NavLink>
                  <NavLink
                    to="/reputation-manager/reviews"
                    className={({ isActive }) =>
                      `tw-text-sm tw-font-bold tw-transition-colors tw-text-decoration-none tw-whitespace-nowrap ${
                        isActive
                          ? "tw-text-[#5D44FF]"
                          : "tw-text-slate-600 hover:tw-text-[#5D44FF]"
                      }`
                    }
                  >
                    Reputation Manager
                  </NavLink>
                </>
              )}
            </nav>
          )}

          {/* Right Action Block */}
          <div className="tw-flex tw-items-center tw-gap-4">
            {isAuthenticated ? (
              <div className="tw-flex tw-items-center tw-gap-4">
                <NotificationDropdown />

                <Popover
                  content={content}
                  trigger="hover"
                  placement="bottomRight"
                >
                  <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-text-[#5D44FF] tw-border tw-border-solid tw-border-indigo-100 hover:tw-bg-indigo-100 tw-transition-colors tw-cursor-pointer">
                    <User className="tw-w-5 tw-h-5" />
                  </div>
                </Popover>

                {isMobile && (
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="tw-p-2 tw-text-slate-600 hover:tw-text-[#5D44FF] tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer"
                  >
                    <Menu className="tw-w-5 tw-h-5" />
                  </button>
                )}
              </div>
            ) : (
              <div className="tw-flex tw-items-center tw-gap-3 xl:tw-gap-4">
                <button
                  onClick={() => navigate("/signin")}
                  className="tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 xl:tw-px-5 xl:tw-py-2.5 tw-bg-transparent hover:tw-bg-indigo-50/50 tw-text-[#5D44FF] hover:tw-text-indigo-600 tw-font-bold tw-text-sm tw-rounded-xl tw-border tw-border-solid tw-border-[#5D44FF] hover:tw-border-indigo-600 tw-transition-all hover:tw-scale-[1.02] tw-outline-none tw-cursor-pointer tw-whitespace-nowrap"
                >
                  Login
                </button>

                {!isCompanyDetailsPage && (
                  <button
                    onClick={() => navigate("/signup")}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 xl:tw-px-5 xl:tw-py-2.5 tw-bg-[#5D44FF] hover:tw-bg-indigo-600 tw-text-white tw-font-bold tw-text-sm tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-transition-all hover:tw-scale-[1.02] tw-border-none tw-outline-none tw-cursor-pointer tw-whitespace-nowrap"
                  >
                    Start free trial
                  </button>
                )}

                {isMobile && (
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="tw-p-2 tw-text-slate-600 hover:tw-text-[#5D44FF] tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer"
                  >
                    <Menu className="tw-w-5 tw-h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Modern Mobile Menu Drawer */}
      <Drawer
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        bodyStyle={{ padding: 0 }}
        width={310}
        title={
          <div className="tw-flex tw-items-center tw-justify-between tw-px-2">
            <span className="tw-font-bold tw-text-slate-800 tw-text-base">
              Navigation Menu
            </span>
          </div>
        }
      >
        <div className="tw-flex tw-flex-col tw-h-full tw-bg-white tw-p-6">
          <div className="tw-space-y-7 tw-flex-1 tw-overflow-y-auto tw-pr-1">
            {!isAuthenticated && (
              <>
                <div className="tw-space-y-4">
                  <div className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest">
                    Products
                  </div>
                  <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                    <NavLink
                      to="/hospitality"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-blue-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Hotel className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Hotel Lead Engine
                      </span>
                    </NavLink>

                    <NavLink
                      to="/review-management"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-rose-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Star className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Reputation &amp; Reviews
                      </span>
                    </NavLink>

                    <NavLink
                      to="/sales"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-emerald-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Briefcase className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Sales Pipeline
                      </span>
                    </NavLink>

                    <NavLink
                      to="/marketing"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-purple-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Megaphone className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Marketing Automation
                      </span>
                    </NavLink>

                    <NavLink
                      to="/data-to-lead"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-cyan-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Inbox className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Data-to-Lead
                      </span>
                    </NavLink>

                    <NavLink
                      to="/lead-enrichment"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-amber-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <FlaskConical className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        AI-Lead Enrichment
                      </span>
                    </NavLink>
                  </div>
                </div>

                <div className="tw-space-y-4">
                  <div className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest">
                    Data Search
                  </div>
                  <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                    <NavLink
                      to="/search-company"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-blue-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Building2 className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Company Search
                      </span>
                    </NavLink>

                    <NavLink
                      to="/search-executive"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-[#F8FAFC] tw-border tw-border-solid tw-border-slate-200/70 tw-text-purple-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <UserCheck className="tw-w-4 tw-h-4" />
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Executive Search
                      </span>
                    </NavLink>
                  </div>
                </div>
              </>
            )}

            <div className="tw-space-y-4">
              <div className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest">
                Navigation
              </div>
              <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                <NavLink
                  to={isAuthenticated ? "/search-lead" : "/lead-management"}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                      isActive
                        ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                        : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                    }`
                  }
                >
                  <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-slate-50 tw-text-slate-500 tw-flex tw-items-center tw-justify-center tw-text-sm tw-flex-shrink-0">
                    🎯
                  </span>
                  <span className="tw-text-sm tw-font-semibold">Leads</span>
                </NavLink>

                {!isAuthenticated && (
                  <NavLink
                    to="/pricing"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                        isActive
                          ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                          : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                      }`
                    }
                  >
                    <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-slate-50 tw-text-slate-500 tw-flex tw-items-center tw-justify-center tw-text-sm tw-flex-shrink-0">
                      🏷️
                    </span>
                    <span className="tw-text-sm tw-font-semibold">Pricing</span>
                  </NavLink>
                )}

                {isAuthenticated && (
                  <>
                    <NavLink
                      to="/dashboard-view"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-slate-50 tw-text-slate-500 tw-flex tw-items-center tw-justify-center tw-text-sm tw-flex-shrink-0">
                        📊
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Dashboard
                      </span>
                    </NavLink>
                    <NavLink
                      to="/reputation-manager/reviews"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `tw-flex tw-items-center tw-gap-3.5 tw-p-2 tw-rounded-xl tw-transition-colors tw-text-decoration-none ${
                          isActive
                            ? "tw-bg-slate-50 tw-text-[#5D44FF]"
                            : "tw-text-slate-700 hover:tw-bg-slate-50/55 hover:tw-text-[#5D44FF]"
                        }`
                      }
                    >
                      <span className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-slate-50 tw-text-slate-500 tw-flex tw-items-center tw-justify-center tw-text-sm tw-flex-shrink-0">
                        🛡️
                      </span>
                      <span className="tw-text-sm tw-font-semibold">
                        Reputation Manager
                      </span>
                    </NavLink>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="tw-mt-auto tw-pt-6 tw-border-t tw-border-slate-100 tw-space-y-3">
            {!isAuthenticated ? (
              <>
                <button
                  onClick={() => {
                    navigate("/signin");
                    setMobileMenuOpen(false);
                  }}
                  className="tw-w-full tw-py-2.5 tw-bg-transparent hover:tw-bg-indigo-50/50 tw-text-[#5D44FF] hover:tw-text-indigo-600 tw-font-bold tw-text-sm tw-rounded-xl tw-border tw-border-solid tw-border-[#5D44FF] hover:tw-border-indigo-600 tw-transition-colors tw-cursor-pointer tw-outline-none"
                >
                  Login
                </button>
                {!isCompanyDetailsPage && (
                  <button
                    onClick={() => {
                      navigate("/signup");
                      setMobileMenuOpen(false);
                    }}
                    className="tw-w-full tw-py-2.5 tw-bg-[#5D44FF] hover:tw-bg-indigo-600 tw-text-white tw-font-bold tw-text-sm tw-rounded-xl tw-border-none tw-cursor-pointer tw-outline-none tw-shadow-sm"
                  >
                    Start Free Trial
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="tw-w-full tw-py-2.5 tw-bg-rose-50 hover:tw-bg-rose-100 tw-text-rose-600 tw-font-semibold tw-text-sm tw-rounded-xl tw-border tw-border-solid tw-border-rose-100 tw-cursor-pointer tw-outline-none"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AppHeader;
